import { memo, useMemo } from "react";
import { Home, Compass, Users2, User, HeartHandshake, LucideIcon } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { usePendingFriendRequests } from "@/hooks/usePendingFriendRequests";
import { useTourStore } from "@/hooks/useTour";
import { tourSteps } from "@/components/tour/tourSteps";
import { cn } from "@/lib/utils";

// Memoized badge component to prevent re-renders when counts don't change
const TabBadge = memo(function TabBadge({ 
  count 
}: { 
  count: number;
}) {
  if (count === 0) return null;
  
  return (
    <div className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-primary rounded-full flex items-center justify-center">
      <span className="text-[10px] font-bold text-primary-foreground px-1">
        {count}
      </span>
    </div>
  );
});

// Memoized tab item to prevent re-renders
interface TabItemProps {
  name: string;
  Icon: LucideIcon;
  path: string;
  tabId: string;
  isHighlighted: boolean;
  badgeCount: number;
}

const TabItem = memo(function TabItem({ 
  name, 
  Icon, 
  path, 
  tabId, 
  isHighlighted,
  badgeCount 
}: TabItemProps) {
  return (
    <NavLink
      to={path}
      data-tour-id={`${tabId}-tab`}
      className={cn(
        "flex flex-col items-center justify-center gap-1.5 px-4 py-2 text-muted-foreground transition-all tap-scale relative",
        isHighlighted && "ring-2 ring-primary ring-offset-2 ring-offset-card rounded-lg"
      )}
      activeClassName="text-primary [&_span]:font-semibold [&_svg]:stroke-[2.25]"
    >
      <div className={cn(
        "relative transition-transform",
        isHighlighted && "animate-pulse scale-110"
      )}>
        <Icon className="w-[22px] h-[22px] stroke-[1.75]" />
        <TabBadge count={badgeCount} />
      </div>
      <span className="text-[10px] font-medium tracking-[0.08em] uppercase transition-all">{name}</span>
    </NavLink>
  );
});

const TABS = [
  { name: "Home", icon: Home, path: "/", tabId: "home" },
  { name: "Events", icon: Compass, path: "/events", tabId: "events" },
  { name: "Herds", icon: Users2, path: "/herds", tabId: "herds" },
  { name: "Connect", icon: HeartHandshake, path: "/social", tabId: "social" },
  { name: "Profile", icon: User, path: "/me", tabId: "profile" },
] as const;

export const TabBar = memo(function TabBar() {
  const unreadCount = useUnreadMessages();
  const { pendingCount: pendingFriendRequests } = usePendingFriendRequests();
  const { isActive: isTourActive, currentStepIndex } = useTourStore();
  
  // Memoize highlighted tab calculation
  const highlightedTab = useMemo(() => {
    if (!isTourActive) return null;
    return tourSteps[currentStepIndex]?.highlightTab;
  }, [isTourActive, currentStepIndex]);

  // Connect combines messages + friend requests.
  const getBadgeCount = useMemo(() => (tabName: string) => {
    if (tabName === "Connect") return unreadCount + pendingFriendRequests;
    return 0;
  }, [unreadCount, pendingFriendRequests]);

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-t border-border shadow-[0_-6px_20px_-12px_hsl(30_25%_28%_/_0.25)]"
      style={{ paddingBottom: 'var(--safe-area-bottom, 0px)' }}
    >
      <div className="flex items-center justify-around h-16 max-w-lg mx-auto px-2">
        {TABS.map((tab) => (
          <TabItem
            key={tab.path}
            name={tab.name}
            Icon={tab.icon}
            path={tab.path}
            tabId={tab.tabId}
            isHighlighted={highlightedTab === tab.tabId}
            badgeCount={getBadgeCount(tab.name)}
          />
        ))}
      </div>
    </nav>
  );
});
